import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import type { ReportExportColumn } from '@/pages/reports/format-report-export';
import { downloadBlob } from '@/utils/download-blob';
import {
  drawPdfBrandHeaderRow,
  drawPdfPageFooters,
  drawRepeatPageHeader,
  loadReportPdfLogo,
  measureRepeatPageHeaderHeight,
  PDF_BRAND_TO_TITLE_GAP,
  PDF_FOOTER_ZONE,
  PDF_PAGE_MARGIN,
  toPdfText,
} from '@/utils/pdf-brand-header';
import { ensurePdfFonts, pdfFont } from '@/utils/pdf-fonts';

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

export interface ReportPdfInput {
  churchName: string;
  /** Product name in the page footer (right). Church name stays in the header. */
  footerLabel: string;
  /** Campus or branch label shown under the church name on the left. */
  branchName?: string | null;
  logoUrl?: string | null;
  title: string;
  description: string;
  periodLabel?: string;
  generatedLabel: string;
  omittedColumnsNote?: string;
  columns: ReportExportColumn[];
  columnLabels: string[];
  rows: string[][];
  totalsLines?: string[];
  filename: string;
}

export interface ReportPdfTableSection {
  title: string;
  columns: ReportExportColumn[];
  columnLabels: string[];
  rows: string[][];
}

export interface CombinedReportPdfInput {
  churchName: string;
  footerLabel: string;
  branchName?: string | null;
  logoUrl?: string | null;
  title: string;
  description: string;
  periodLabel?: string;
  generatedLabel: string;
  tableSections: ReportPdfTableSection[];
  filename: string;
}

const drawReportTitleBlock = (
  doc: jsPDF,
  startY: number,
  leftWidth: number,
  input: Pick<
    ReportPdfInput,
    'title' | 'description' | 'periodLabel' | 'generatedLabel' | 'omittedColumnsNote'
  >,
): number => {
  let y = startY;

  doc.setFontSize(16);
  doc.setFont(pdfFont(), 'bold');
  doc.setTextColor(40, 40, 40);
  const titleLines = doc.splitTextToSize(toPdfText(input.title), leftWidth);
  doc.text(titleLines, PDF_PAGE_MARGIN, y);
  y += titleLines.length * 6 + 1;

  doc.setFont(pdfFont(), 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  const descriptionLines = doc.splitTextToSize(toPdfText(input.description), leftWidth);
  doc.text(descriptionLines, PDF_PAGE_MARGIN, y);
  y += descriptionLines.length * 4 + 2;

  if (input.periodLabel) {
    doc.text(toPdfText(input.periodLabel), PDF_PAGE_MARGIN, y);
    y += 4;
  }

  doc.text(toPdfText(input.generatedLabel), PDF_PAGE_MARGIN, y);
  y += 4;

  if (input.omittedColumnsNote) {
    const noteLines = doc.splitTextToSize(toPdfText(input.omittedColumnsNote), leftWidth);
    doc.text(noteLines, PDF_PAGE_MARGIN, y);
    y += noteLines.length * 4;
  }

  return y;
};

export const downloadReportPdf = async (input: ReportPdfInput): Promise<void> => {
  const logo = await loadReportPdfLogo(input.logoUrl);
  const branchName = input.branchName?.trim() || null;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' }) as JsPdfWithAutoTable;
  await ensurePdfFonts(doc);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PDF_PAGE_MARGIN * 2;
  const leftWidth = contentWidth;
  const repeatHeaderHeight = measureRepeatPageHeaderHeight(
    doc,
    pageWidth,
    input.title,
    input.churchName,
    branchName,
    logo,
  );

  const brand = drawPdfBrandHeaderRow(doc, pageWidth, PDF_PAGE_MARGIN, {
    churchName: input.churchName,
    branchName,
    logo,
  });

  let y = drawReportTitleBlock(
    doc,
    brand.leftBottomY + PDF_BRAND_TO_TITLE_GAP,
    leftWidth,
    input,
  );

  y = Math.max(y, brand.blockBottomY) + 2;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(PDF_PAGE_MARGIN, y, pageWidth - PDF_PAGE_MARGIN, y);
  y += 4;

  const columnStyles: Record<number, { halign?: 'left' | 'right' | 'center'; cellWidth?: number | 'auto' }> =
    {};
  for (let index = 0; index < input.columns.length; index += 1) {
    const column = input.columns[index]!;
    if (column.type === 'money' || column.type === 'number') {
      columnStyles[index] = { halign: 'right' };
    }
  }

  autoTable(doc, {
    startY: y,
    head: [input.columnLabels.map((label) => toPdfText(label))],
    body:
      input.rows.length > 0
        ? input.rows.map((row) => row.map((cell) => toPdfText(cell)))
        : [['—']],
    theme: 'grid',
    styles: {
      font: pdfFont(),
      fontSize: 8,
      textColor: [40, 40, 40],
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
      valign: 'top',
      cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
      overflow: 'linebreak',
    },
    headStyles: {
      font: pdfFont(),
      fontStyle: 'bold',
      fillColor: [245, 245, 245],
      textColor: [40, 40, 40],
    },
    columnStyles,
    margin: {
      top: repeatHeaderHeight,
      left: PDF_PAGE_MARGIN,
      right: PDF_PAGE_MARGIN,
      bottom: PDF_PAGE_MARGIN + PDF_FOOTER_ZONE,
    },
    didDrawPage: (data) => {
      if (data.pageNumber === 1) return;
      drawRepeatPageHeader(doc, pageWidth, input.title, input.churchName, branchName, logo);
    },
  });

  if (input.totalsLines && input.totalsLines.length > 0) {
    let totalsY = (doc.lastAutoTable?.finalY ?? y) + 6;
    if (totalsY > pageHeight - PDF_PAGE_MARGIN - PDF_FOOTER_ZONE - 20) {
      doc.addPage();
      totalsY = drawRepeatPageHeader(doc, pageWidth, input.title, input.churchName, branchName, logo);
    }

    doc.setFont(pdfFont(), 'bold');
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    for (const line of input.totalsLines) {
      doc.text(toPdfText(line), PDF_PAGE_MARGIN, totalsY);
      totalsY += 5;
    }
  }

  drawPdfPageFooters(doc, pageWidth, pageHeight, input.footerLabel);
  downloadBlob(doc.output('blob'), input.filename);
};

export interface NarrativeReportPdfInput {
  churchName: string;
  footerLabel: string;
  branchName?: string | null;
  logoUrl?: string | null;
  title: string;
  metaLines: string[];
  body: string;
  generatedLabel: string;
  filename: string;
}

/** A written report: title, a few facts, then the narrative. No table and no images. */
export const downloadNarrativeReportPdf = async (input: NarrativeReportPdfInput): Promise<void> => {
  const logo = await loadReportPdfLogo(input.logoUrl);
  const branchName = input.branchName?.trim() || null;
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  await ensurePdfFonts(doc);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PDF_PAGE_MARGIN * 2;
  const bottom = pageHeight - PDF_PAGE_MARGIN - PDF_FOOTER_ZONE;

  const brand = drawPdfBrandHeaderRow(doc, pageWidth, PDF_PAGE_MARGIN, {
    churchName: input.churchName,
    branchName,
    logo,
  });

  let y = brand.leftBottomY + PDF_BRAND_TO_TITLE_GAP;
  doc.setFont(pdfFont(), 'bold');
  doc.setFontSize(16);
  doc.setTextColor(40, 40, 40);
  const titleLines = doc.splitTextToSize(toPdfText(input.title), contentWidth) as string[];
  doc.text(titleLines, PDF_PAGE_MARGIN, y);
  y += titleLines.length * 7 + 3;

  doc.setFont(pdfFont(), 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90, 90, 90);
  for (const meta of input.metaLines) {
    const lines = doc.splitTextToSize(toPdfText(meta), contentWidth) as string[];
    doc.text(lines, PDF_PAGE_MARGIN, y);
    y += lines.length * 5;
  }

  y += 2;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(PDF_PAGE_MARGIN, y, pageWidth - PDF_PAGE_MARGIN, y);
  y += 8;

  doc.setFontSize(11);
  doc.setTextColor(40, 40, 40);
  const paragraphs = (input.body.trim() || '—').split('\n');
  for (const paragraph of paragraphs) {
    const lines = doc.splitTextToSize(toPdfText(paragraph.length > 0 ? paragraph : ' '), contentWidth) as string[];
    for (const line of lines) {
      if (y > bottom) {
        doc.addPage();
        y = drawRepeatPageHeader(doc, pageWidth, input.title, input.churchName, branchName, logo);
      }
      doc.text(line, PDF_PAGE_MARGIN, y);
      y += 5.5;
    }
    y += 2;
  }

  y += 4;
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  if (y > bottom) {
    doc.addPage();
    y = drawRepeatPageHeader(doc, pageWidth, input.title, input.churchName, branchName, logo);
  }
  doc.text(toPdfText(input.generatedLabel), PDF_PAGE_MARGIN, y);

  drawPdfPageFooters(doc, pageWidth, pageHeight, input.footerLabel);
  downloadBlob(doc.output('blob'), input.filename);
};

export interface ChronicleTimelinePdfMoment {
  year: string;
  dateLabel: string;
  title: string;
  meta: string;
  body: string;
}

export interface ChronicleTimelinePdfInput {
  churchName: string;
  footerLabel: string;
  branchName?: string | null;
  logoUrl?: string | null;
  title: string;
  description: string;
  periodLabel?: string;
  generatedLabel: string;
  moments: ChronicleTimelinePdfMoment[];
  filename: string;
}

/** Chronicle moments down the page by year. No table and no images. */
export const downloadChronicleTimelinePdf = async (
  input: ChronicleTimelinePdfInput,
): Promise<void> => {
  const logo = await loadReportPdfLogo(input.logoUrl);
  const branchName = input.branchName?.trim() || null;
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  await ensurePdfFonts(doc);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PDF_PAGE_MARGIN * 2;
  const bottom = pageHeight - PDF_PAGE_MARGIN - PDF_FOOTER_ZONE;
  const textLeft = PDF_PAGE_MARGIN + 6;
  const textWidth = contentWidth - 6;

  const continuePage = (y: number): number => {
    if (y <= bottom) return y;
    doc.addPage();
    return drawRepeatPageHeader(doc, pageWidth, input.title, input.churchName, branchName, logo);
  };

  const brand = drawPdfBrandHeaderRow(doc, pageWidth, PDF_PAGE_MARGIN, {
    churchName: input.churchName,
    branchName,
    logo,
  });

  let y = brand.leftBottomY + PDF_BRAND_TO_TITLE_GAP;
  doc.setFont(pdfFont(), 'bold');
  doc.setFontSize(16);
  doc.setTextColor(40, 40, 40);
  const titleLines = doc.splitTextToSize(toPdfText(input.title), contentWidth) as string[];
  doc.text(titleLines, PDF_PAGE_MARGIN, y);
  y += titleLines.length * 7 + 2;

  doc.setFont(pdfFont(), 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90, 90, 90);
  for (const line of [input.description, input.periodLabel, input.generatedLabel]) {
    if (!line) continue;
    const wrapped = doc.splitTextToSize(toPdfText(line), contentWidth) as string[];
    doc.text(wrapped, PDF_PAGE_MARGIN, y);
    y += wrapped.length * 5;
  }

  y += 2;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(PDF_PAGE_MARGIN, y, pageWidth - PDF_PAGE_MARGIN, y);
  y += 8;

  let currentYear = '';
  const moments = input.moments.length > 0 ? input.moments : [];
  if (moments.length === 0) {
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text('—', PDF_PAGE_MARGIN, y);
  }

  for (const moment of moments) {
    if (moment.year !== currentYear) {
      currentYear = moment.year;
      y = continuePage(y + 2);
      doc.setFont(pdfFont(), 'bold');
      doc.setFontSize(13);
      doc.setTextColor(40, 40, 40);
      doc.text(toPdfText(moment.year), PDF_PAGE_MARGIN, y);
      y += 7;
    }

    y = continuePage(y);
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.4);
    doc.line(PDF_PAGE_MARGIN + 1.5, y - 3, PDF_PAGE_MARGIN + 1.5, y + 2);
    doc.circle(PDF_PAGE_MARGIN + 1.5, y - 1, 0.8, 'F');

    doc.setFont(pdfFont(), 'normal');
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    const dateLines = doc.splitTextToSize(toPdfText(moment.dateLabel), textWidth) as string[];
    doc.text(dateLines, textLeft, y);
    y += dateLines.length * 4.5 + 1;

    y = continuePage(y);
    doc.setFont(pdfFont(), 'bold');
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    const titleLinesForMoment = doc.splitTextToSize(toPdfText(moment.title), textWidth) as string[];
    doc.text(titleLinesForMoment, textLeft, y);
    y += titleLinesForMoment.length * 5 + 1;

    if (moment.meta) {
      y = continuePage(y);
      doc.setFont(pdfFont(), 'normal');
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      const metaLines = doc.splitTextToSize(toPdfText(moment.meta), textWidth) as string[];
      doc.text(metaLines, textLeft, y);
      y += metaLines.length * 4.5;
    }

    if (moment.body) {
      y += 1;
      doc.setFont(pdfFont(), 'normal');
      doc.setFontSize(10);
      doc.setTextColor(40, 40, 40);
      for (const paragraph of moment.body.split('\n')) {
        const lines = doc.splitTextToSize(
          toPdfText(paragraph.length > 0 ? paragraph : ' '),
          textWidth,
        ) as string[];
        for (const line of lines) {
          y = continuePage(y);
          doc.text(line, textLeft, y);
          y += 5;
        }
      }
    }

    y += 5;
  }

  drawPdfPageFooters(doc, pageWidth, pageHeight, input.footerLabel);
  downloadBlob(doc.output('blob'), input.filename);
};

const drawSectionTitleRow = (
  doc: JsPdfWithAutoTable,
  pageWidth: number,
  title: string,
  columnCount: number,
  startY: number,
): number => {
  autoTable(doc, {
    startY,
    head: [[{ content: toPdfText(title), colSpan: columnCount, styles: { halign: 'left' } }]],
    body: [],
    theme: 'plain',
    styles: {
      font: pdfFont(),
      fontStyle: 'bold',
      fontSize: 10,
      fillColor: [245, 245, 245],
      textColor: [40, 40, 40],
      cellPadding: { top: 3, right: 2, bottom: 3, left: 2 },
    },
    margin: {
      top: PDF_PAGE_MARGIN,
      left: PDF_PAGE_MARGIN,
      right: PDF_PAGE_MARGIN,
      bottom: PDF_PAGE_MARGIN + PDF_FOOTER_ZONE,
    },
    tableWidth: pageWidth - PDF_PAGE_MARGIN * 2,
  });
  return doc.lastAutoTable?.finalY ?? startY;
};

const drawPdfTableSection = (
  doc: JsPdfWithAutoTable,
  pageWidth: number,
  section: ReportPdfTableSection,
  startY: number,
  repeatHeader: (doc: jsPDF) => void,
  repeatHeaderHeight: number,
): number => {
  const columnCount = Math.max(section.columns.length, 1);
  let y = drawSectionTitleRow(doc, pageWidth, section.title, columnCount, startY);
  y += 2;

  const columnStyles: Record<number, { halign?: 'left' | 'right' | 'center' }> = {};
  for (let index = 0; index < section.columns.length; index += 1) {
    const column = section.columns[index]!;
    if (column.type === 'money' || column.type === 'number') {
      columnStyles[index] = { halign: 'right' };
    }
  }

  autoTable(doc, {
    startY: y,
    head: [section.columnLabels.map((label) => toPdfText(label))],
    body:
      section.rows.length > 0
        ? section.rows.map((row) => row.map((cell) => toPdfText(cell)))
        : [['—']],
    theme: 'grid',
    styles: {
      font: pdfFont(),
      fontSize: 8,
      textColor: [40, 40, 40],
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
      valign: 'top',
      cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
      overflow: 'linebreak',
    },
    headStyles: {
      font: pdfFont(),
      fontStyle: 'bold',
      fillColor: [245, 245, 245],
      textColor: [40, 40, 40],
    },
    columnStyles,
    margin: {
      top: repeatHeaderHeight,
      left: PDF_PAGE_MARGIN,
      right: PDF_PAGE_MARGIN,
      bottom: PDF_PAGE_MARGIN + PDF_FOOTER_ZONE,
    },
    didDrawPage: (data) => {
      if (data.pageNumber === 1) return;
      repeatHeader(doc);
    },
  });

  return (doc.lastAutoTable?.finalY ?? y) + 8;
};

export const downloadCombinedReportPdf = async (input: CombinedReportPdfInput): Promise<void> => {
  const logo = await loadReportPdfLogo(input.logoUrl);
  const branchName = input.branchName?.trim() || null;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' }) as JsPdfWithAutoTable;
  await ensurePdfFonts(doc);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PDF_PAGE_MARGIN * 2;
  const leftWidth = contentWidth;

  const repeatHeaderHeight = measureRepeatPageHeaderHeight(
    doc,
    pageWidth,
    input.title,
    input.churchName,
    branchName,
    logo,
  );
  const repeatHeader = (pdfDoc: jsPDF) =>
    drawRepeatPageHeader(pdfDoc, pageWidth, input.title, input.churchName, branchName, logo);

  const brand = drawPdfBrandHeaderRow(doc, pageWidth, PDF_PAGE_MARGIN, {
    churchName: input.churchName,
    branchName,
    logo,
  });

  let y = drawReportTitleBlock(
    doc,
    brand.leftBottomY + PDF_BRAND_TO_TITLE_GAP,
    leftWidth,
    input,
  );

  y = Math.max(y, brand.blockBottomY) + 2;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(PDF_PAGE_MARGIN, y, pageWidth - PDF_PAGE_MARGIN, y);
  y += 4;

  for (const section of input.tableSections) {
    if (y > pageHeight - PDF_PAGE_MARGIN - PDF_FOOTER_ZONE - 30) {
      doc.addPage();
      y = repeatHeader(doc);
    }
    y = drawPdfTableSection(doc, pageWidth, section, y, repeatHeader, repeatHeaderHeight);
  }

  drawPdfPageFooters(doc, pageWidth, pageHeight, input.footerLabel);
  downloadBlob(doc.output('blob'), input.filename);
};
